import { zodResolver } from "@hookform/resolvers/zod";
import {
  type AccountMapping,
  BUSINESS_UNIT_TYPES,
  type BusinessUnitType,
  createAccountMappingSchema,
  type PostingException,
  type PostingKey,
  updateAccountMappingSchema,
} from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  Dialog,
  EmptyState,
  FormField,
  Input,
  MoneyText,
  Select,
  SkeletonRows,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  useToast,
} from "@station/ui";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { CircleCheck, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { AccountCombobox, type AccountOption } from "../../../features/accounting/account-combobox.js";
import { AccountingPage, companySearchShape } from "../../../features/accounting/company.js";
import { useCompanyStations, usePendingExceptionCount } from "../../../features/accounting/shared.js";
import { zodErrorMap } from "../../../features/accounting/validation.js";
import { api } from "../../../lib/api.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { applyFieldErrors, errorMessage } from "../../../lib/errors.js";
import { formatDate, formatDateTime } from "../../../lib/format.js";
import { queryKeys } from "../../../lib/query.js";
import { usePermissions } from "../../../lib/session.js";

const searchSchema = z.object({
  ...companySearchShape,
  tab: z.enum(["mappings", "exceptions"]).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/muhasebe/eslestirmeler")({
  validateSearch: (search) => searchSchema.parse(search),
  component: MappingsPage,
});

function MappingsPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  return (
    <AccountingPage title={t("menu.accountMappings")} description={t("accounting.mappings.description")}>
      {({ companyId }) => (
        <Tabs
          value={search.tab ?? "mappings"}
          onValueChange={(tab) =>
            void navigate({
              search: (previous) => ({
                company: previous.company,
                ...(tab === "exceptions" ? { tab: "exceptions" as const } : {}),
              }),
              replace: true,
            })
          }
        >
          <TabsList aria-label={t("menu.accountMappings")}>
            <TabsTrigger value="mappings">{t("accounting.mappings.tabs.mappings")}</TabsTrigger>
            <TabsTrigger value="exceptions">
              {t("accounting.mappings.tabs.exceptions")}
              <PendingBadge companyId={companyId} />
            </TabsTrigger>
          </TabsList>
          <TabsContent value="mappings">
            <Mappings companyId={companyId} />
          </TabsContent>
          <TabsContent value="exceptions">
            <Exceptions companyId={companyId} />
          </TabsContent>
        </Tabs>
      )}
    </AccountingPage>
  );
}

function PendingBadge({ companyId }: { companyId: string }): ReactElement | null {
  const { t } = useTranslation();
  const count = usePendingExceptionCount(companyId);
  if (!count) return null;
  return (
    <span className="ml-2 rounded-full bg-warning-subtle px-2 py-0.5 text-xs font-semibold text-warning">
      {count}
      <span className="sr-only"> {t("accounting.exceptions.pendingSuffix")}</span>
    </span>
  );
}

function usePostingKeys(companyId: string) {
  return useQuery({
    queryKey: queryKeys.accounting(companyId, "posting-keys"),
    staleTime: 3_600_000,
    queryFn: async () => (await api.GET("/accounting/posting-keys")).data!.items,
  });
}

/** Eşleştirmeler kayıt anahtarına göre gruplanır; her grupta en ÖZEL satır en üsttedir (çözümleme sırası). */
function Mappings({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const canManage = usePermissions().has("accounting.mapping.manage");
  const keys = usePostingKeys(companyId);
  const mappings = useQuery({
    queryKey: queryKeys.accounting(companyId, "mappings"),
    queryFn: async () =>
      (await api.GET("/accounting/mappings", { params: { query: { companyId } } })).data!.items,
  });
  const [dialog, setDialog] = useState<{ mapping: AccountMapping | null; key?: PostingKey } | null>(null);
  const [removing, setRemoving] = useState<AccountMapping | null>(null);

  const groups = useMemo(() => {
    const specificity = (mapping: AccountMapping) =>
      (mapping.keyParam ? 4 : 0) + (mapping.stationId ? 2 : 0) + (mapping.businessUnit ? 1 : 0);
    return (keys.data ?? []).map((key) => ({
      key,
      items: (mappings.data ?? [])
        .filter((mapping) => mapping.postingKey === key.key)
        .sort((left, right) => specificity(right) - specificity(left)),
    }));
  }, [keys.data, mappings.data]);

  const error = keys.error ?? mappings.error;
  if (error) return <Alert tone="danger">{errorMessage(error)}</Alert>;
  if (!keys.data || !mappings.data) return <SkeletonRows rows={8} />;

  return (
    <div className="grid gap-4">
      <Alert tone="info">{t("accounting.mappings.resolutionNote")}</Alert>
      {canManage ? (
        <div className="flex justify-end">
          <Button onClick={() => setDialog({ mapping: null })}>
            <Plus aria-hidden="true" />
            {t("accounting.mappings.add")}
          </Button>
        </div>
      ) : null}
      <Card className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <caption className="sr-only">{t("accounting.mappings.tabs.mappings")}</caption>
          <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th scope="col" className="px-3 py-2 text-left font-semibold">
                {t("accounting.mappings.specialization")}
              </th>
              <th scope="col" className="px-3 py-2 text-left font-semibold">
                {t("accounting.fields.account")}
              </th>
              <th scope="col" className="px-3 py-2 text-left font-semibold">
                {t("accounting.mappings.note")}
              </th>
              <th scope="col" className="px-3 py-2 text-right font-semibold">
                <span className="sr-only">{t("common.actions")}</span>
              </th>
            </tr>
          </thead>
          {groups.map(({ key, items }) => (
            <tbody key={key.key} className="border-t">
              <tr className="bg-muted/30">
                <th scope="rowgroup" colSpan={4} className="px-3 py-2 text-left">
                  <span className="font-semibold">{key.label}</span>{" "}
                  <span className="font-mono text-xs text-muted-foreground">{key.key}</span>
                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                    {t("accounting.mappings.module", { module: key.module })}
                    {key.param ? ` · ${t("accounting.mappings.param", { param: key.param })}` : ""}
                  </span>
                </th>
              </tr>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-2 text-warning">
                    {t("accounting.mappings.noneForKey")}
                  </td>
                </tr>
              ) : (
                items.map((mapping) => {
                  const parts = [
                    mapping.keyParam,
                    mapping.stationName,
                    mapping.businessUnit ? t(`enums.businessUnit.${mapping.businessUnit}`) : null,
                  ].filter(Boolean);
                  return (
                    <tr key={mapping.id} className="border-t">
                      <td className="px-3 py-2">
                        {parts.length > 0 ? parts.join(" · ") : t("accounting.mappings.general")}
                      </td>
                      <td className="px-3 py-2">
                        <span className="font-mono text-xs font-semibold">{mapping.accountCode}</span>{" "}
                        {mapping.accountName}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{mapping.note ?? "—"}</td>
                      <td className="whitespace-nowrap px-3 py-1 text-right">
                        {canManage ? (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-8"
                              aria-label={t("accounting.mappings.edit", {
                                key: key.label,
                                account: mapping.accountCode,
                              })}
                              onClick={() => setDialog({ mapping })}
                            >
                              <Pencil aria-hidden="true" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-8 hover:text-destructive"
                              aria-label={t("accounting.mappings.remove", {
                                key: key.label,
                                account: mapping.accountCode,
                              })}
                              onClick={() => setRemoving(mapping)}
                            >
                              <Trash2 aria-hidden="true" />
                            </Button>
                          </>
                        ) : null}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          ))}
        </table>
      </Card>

      <Dialog
        open={dialog !== null}
        onOpenChange={(open) => !open && setDialog(null)}
        title={dialog?.mapping ? t("accounting.mappings.editTitle") : t("accounting.mappings.add")}
        description={t("accounting.mappings.formHint")}
      >
        {dialog ? (
          <MappingForm
            key={dialog.mapping?.id ?? "new"}
            companyId={companyId}
            mapping={dialog.mapping}
            keys={keys.data}
            onDone={() => setDialog(null)}
          />
        ) : null}
      </Dialog>
      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        tone="danger"
        title={t("accounting.mappings.removeTitle")}
        description={t("accounting.mappings.removeDescription")}
        confirmLabel={t("accounting.mappings.removeConfirm")}
        onConfirm={async () => {
          if (!removing) return;
          try {
            await api.DELETE("/accounting/mappings/{id}", { params: { path: { id: removing.id } } });
            await queryClient.invalidateQueries({ queryKey: queryKeys.accounting(companyId, "mappings") });
            toast({ tone: "success", title: t("accounting.mappings.removed") });
          } catch (error) {
            toast({ tone: "danger", title: errorMessage(error) });
            throw error;
          }
        }}
      />
    </div>
  );
}

type MappingValues = z.input<typeof createAccountMappingSchema>;

function MappingForm({
  companyId,
  mapping,
  keys,
  onDone,
}: {
  companyId: string;
  mapping: AccountMapping | null;
  keys: ReadonlyArray<{ key: PostingKey; label: string; param: string | null; module: string }>;
  onDone: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const stations = useCompanyStations(companyId);
  const [formError, setFormError] = useState<string | null>(null);
  const [account, setAccount] = useState<AccountOption | null>(
    mapping
      ? {
          id: mapping.accountId,
          code: mapping.accountCode,
          name: mapping.accountName,
          requiredDimensions: [],
          isPostable: true,
        }
      : null,
  );
  const form = useForm<MappingValues, unknown, z.output<typeof createAccountMappingSchema>>({
    // Boş bırakılan isteğe bağlı alanlar doğrulamadan ÖNCE null'a çevrilir (bkz. product-values.ts kalıbı).
    resolver: zodResolver(
      z.preprocess((values) => {
        if (typeof values !== "object" || values === null) return values;
        const next: Record<string, unknown> = { ...values };
        for (const field of ["keyParam", "stationId", "businessUnit", "note"])
          if (next[field] === "") next[field] = null;
        return next;
      }, createAccountMappingSchema) as unknown as typeof createAccountMappingSchema,
      { error: zodErrorMap },
    ),
    defaultValues: {
      companyId,
      postingKey: mapping?.postingKey ?? keys[0]!.key,
      keyParam: mapping?.keyParam ?? "",
      stationId: mapping?.stationId ?? "",
      businessUnit: mapping?.businessUnit ?? ("" as unknown as BusinessUnitType),
      accountId: mapping?.accountId ?? "",
      note: mapping?.note ?? "",
    },
  });
  const { errors, isSubmitting } = form.formState;
  const selectedKey = keys.find((item) => item.key === form.watch("postingKey"));

  const submit = form.handleSubmit(async (values) => {
    setFormError(null);
    try {
      if (mapping)
        await api.PATCH("/accounting/mappings/{id}", {
          params: { path: { id: mapping.id } },
          body: updateAccountMappingSchema.parse({
            accountId: values.accountId,
            note: values.note,
            version: mapping.version,
          }),
        });
      else await api.POST("/accounting/mappings", { body: values });
      await queryClient.invalidateQueries({ queryKey: queryKeys.accounting(companyId) });
      toast({ tone: "success", title: t("common.saved") });
      onDone();
    } catch (error) {
      const unmatched = applyFieldErrors(error, form.setError, [
        "postingKey",
        "keyParam",
        "stationId",
        "businessUnit",
        "accountId",
        "note",
      ]);
      if (unmatched.length > 0 || Object.keys(form.formState.errors).length === 0)
        setFormError(errorMessage(error));
    }
  });

  return (
    <form className="grid gap-4" noValidate onSubmit={(event) => void submit(event)}>
      {formError ? <Alert tone="danger">{formError}</Alert> : null}
      <FormField label={t("accounting.mappings.key")} error={errors.postingKey?.message} required>
        {(control) => (
          <Select
            {...control}
            {...form.register("postingKey")}
            disabled={mapping !== null}
            autoFocus={!mapping}
          >
            {keys.map((item) => (
              <option key={item.key} value={item.key}>
                {item.label} ({item.module})
              </option>
            ))}
          </Select>
        )}
      </FormField>
      <div className="grid gap-4 sm:grid-cols-3">
        <FormField
          label={t("accounting.mappings.keyParam")}
          description={selectedKey?.param ?? t("accounting.mappings.keyParamNone")}
          error={errors.keyParam?.message}
        >
          {(control) => (
            <Input
              {...control}
              {...form.register("keyParam")}
              className="font-mono"
              disabled={mapping !== null || !selectedKey?.param}
            />
          )}
        </FormField>
        <FormField label={t("accounting.dimensions.STATION")} error={errors.stationId?.message}>
          {(control) => (
            <Select {...control} {...form.register("stationId")} disabled={mapping !== null}>
              <option value="">{t("common.all")}</option>
              {(stations.data ?? []).map((station) => (
                <option key={station.id} value={station.id}>
                  {station.code} · {station.name}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        <FormField label={t("accounting.dimensions.BUSINESS_UNIT")} error={errors.businessUnit?.message}>
          {(control) => (
            <Select {...control} {...form.register("businessUnit")} disabled={mapping !== null}>
              <option value="">{t("common.all")}</option>
              {BUSINESS_UNIT_TYPES.map((unit) => (
                <option key={unit} value={unit}>
                  {t(`enums.businessUnit.${unit}`)}
                </option>
              ))}
            </Select>
          )}
        </FormField>
      </div>
      {mapping ? (
        <p className="text-xs text-muted-foreground">{t("accounting.mappings.lockedHint")}</p>
      ) : null}
      <FormField label={t("accounting.fields.account")} error={errors.accountId?.message} required>
        {(control) => (
          <AccountCombobox
            {...control}
            companyId={companyId}
            value={account}
            onChange={(next) => {
              setAccount(next);
              form.setValue("accountId", next?.id ?? "", {
                shouldDirty: true,
                shouldValidate: form.formState.isSubmitted,
              });
            }}
          />
        )}
      </FormField>
      <FormField label={t("accounting.mappings.note")} error={errors.note?.message}>
        {(control) => <Input {...control} {...form.register("note")} maxLength={300} />}
      </FormField>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onDone}>
          {t("common.cancel")}
        </Button>
        <Button type="submit" loading={isSubmitting}>
          {t("common.save")}
        </Button>
      </div>
    </form>
  );
}

/** Eşleştirme bekleyenler: kayıt motorunun fiş ÜRETEMEDİĞİ istekler. Eşleştirme tamamlanınca yeniden işlenir. */
function Exceptions({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const canManage = usePermissions().has("accounting.mapping.manage");
  const pages = useCursorPages(companyId);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dismissing, setDismissing] = useState<PostingException | null>(null);
  const [reason, setReason] = useState("");

  const exceptions = useQuery({
    queryKey: queryKeys.accounting(companyId, "exceptions", { cursor: pages.cursor ?? null }),
    queryFn: async () =>
      (
        await api.GET("/accounting/posting-exceptions", {
          params: {
            query: {
              companyId,
              status: "PENDING",
              limit: 50,
              ...(pages.cursor ? { cursor: pages.cursor } : {}),
            },
          },
        })
      ).data!,
    placeholderData: keepPreviousData,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: queryKeys.accounting(companyId) });

  const retry = useMutation({
    mutationFn: async (ids: string[] | null) =>
      (
        await api.POST("/accounting/posting-exceptions/retry", {
          body: { companyId, ...(ids ? { ids } : {}) },
        })
      ).data!,
    onSuccess: async (result) => {
      setSelected(new Set());
      await refresh();
      toast({
        tone: result.stillPending > 0 ? "info" : "success",
        title: t("accounting.exceptions.retryResult", {
          attempted: result.attempted,
          resolved: result.resolved,
          pending: result.stillPending,
        }),
      });
    },
    onError: (error) => toast({ tone: "danger", title: errorMessage(error) }),
  });
  const dismiss = useMutation({
    mutationFn: async () =>
      api.POST("/accounting/posting-exceptions/{id}/dismiss", {
        params: { path: { id: dismissing!.id } },
        body: { reason: reason.trim() },
      }),
    onSuccess: async () => {
      setDismissing(null);
      setReason("");
      await refresh();
      toast({ tone: "success", title: t("accounting.exceptions.dismissed") });
    },
    onError: (error) => toast({ tone: "danger", title: errorMessage(error) }),
  });

  if (exceptions.error) return <Alert tone="danger">{errorMessage(exceptions.error)}</Alert>;
  if (!exceptions.data) return <SkeletonRows rows={5} />;
  const rows = exceptions.data.items;
  if (rows.length === 0 && !pages.hasPrevious)
    return (
      <EmptyState
        icon={<CircleCheck />}
        title={t("accounting.exceptions.empty")}
        description={t("accounting.exceptions.emptyHint")}
      />
    );
  const allSelected = rows.length > 0 && rows.every((row) => selected.has(row.id));

  return (
    <div className="grid gap-4">
      <Alert tone="warning">{t("accounting.exceptions.note")}</Alert>
      {canManage ? (
        <div className="flex flex-wrap justify-end gap-2">
          <Button
            variant="outline"
            disabled={selected.size === 0 || retry.isPending}
            onClick={() => retry.mutate([...selected])}
          >
            <RefreshCw aria-hidden="true" />
            {t("accounting.exceptions.retrySelected", { count: selected.size })}
          </Button>
          <Button loading={retry.isPending} onClick={() => retry.mutate(null)}>
            <RefreshCw aria-hidden="true" />
            {t("accounting.exceptions.retryAll")}
          </Button>
        </div>
      ) : null}
      <Card className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <caption className="sr-only">{t("accounting.mappings.tabs.exceptions")}</caption>
          <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              {canManage ? (
                <th scope="col" className="w-10 px-3 py-2">
                  <Checkbox
                    aria-label={t("accounting.exceptions.selectAll")}
                    checked={allSelected}
                    onChange={(event) =>
                      setSelected(event.target.checked ? new Set(rows.map((row) => row.id)) : new Set())
                    }
                  />
                </th>
              ) : null}
              <th scope="col" className="px-3 py-2 text-left font-semibold">
                {t("accounting.fields.postingDate")}
              </th>
              <th scope="col" className="px-3 py-2 text-left font-semibold">
                {t("accounting.fields.description")}
              </th>
              <th scope="col" className="px-3 py-2 text-left font-semibold">
                {t("accounting.exceptions.reason")}
              </th>
              <th scope="col" className="px-3 py-2 text-left font-semibold">
                {t("accounting.exceptions.missingKeys")}
              </th>
              <th scope="col" className="px-3 py-2 text-right font-semibold">
                {t("accounting.fields.amount")}
              </th>
              <th scope="col" className="px-3 py-2 text-right font-semibold">
                {t("accounting.exceptions.attempts")}
              </th>
              <th scope="col" className="px-3 py-2 text-right font-semibold">
                <span className="sr-only">{t("common.actions")}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-t align-top">
                {canManage ? (
                  <td className="px-3 py-2">
                    <Checkbox
                      aria-label={t("accounting.exceptions.select", { description: row.description })}
                      checked={selected.has(row.id)}
                      onChange={(event) =>
                        setSelected((current) => {
                          const next = new Set(current);
                          if (event.target.checked) next.add(row.id);
                          else next.delete(row.id);
                          return next;
                        })
                      }
                    />
                  </td>
                ) : null}
                <td className="whitespace-nowrap px-3 py-2 tabular">{formatDate(row.postingDate)}</td>
                <td className="px-3 py-2">
                  <p className="font-medium">{row.description}</p>
                  <p className="text-xs text-muted-foreground">
                    {t(`enums.journalSource.${row.source}`, { defaultValue: row.source })} ·{" "}
                    {row.sourceDocType}
                  </p>
                </td>
                <td className="px-3 py-2">
                  <StatusBadge tone="warning">{t(`enums.postingExceptionReason.${row.reason}`)}</StatusBadge>
                </td>
                <td className="px-3 py-2">
                  <ul className="grid gap-0.5 font-mono text-xs">
                    {row.missingKeys.map((key) => (
                      <li key={key}>{key}</li>
                    ))}
                  </ul>
                </td>
                <td className="px-3 py-2 text-right">
                  <MoneyText value={row.totalAmount} />
                </td>
                <td className="px-3 py-2 text-right tabular">
                  {row.attempts}
                  <p className="whitespace-nowrap text-xs text-muted-foreground">
                    {formatDateTime(row.lastAttemptAt)}
                  </p>
                </td>
                <td className="px-3 py-1 text-right">
                  {canManage ? (
                    <Button variant="ghost" size="sm" onClick={() => setDismissing(row)}>
                      {t("accounting.exceptions.dismiss")}
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      {pages.hasPrevious || exceptions.data.page.hasMore ? (
        <nav className="flex justify-end gap-2" aria-label={t("accounting.exceptions.pagination")}>
          <Button variant="outline" size="sm" disabled={!pages.hasPrevious} onClick={pages.previous}>
            {t("common.back")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!exceptions.data.page.hasMore}
            onClick={() => pages.next(exceptions.data.page.nextCursor)}
          >
            {t("common.continue")}
          </Button>
        </nav>
      ) : null}

      <Dialog
        open={dismissing !== null}
        onOpenChange={(open) => !open && setDismissing(null)}
        title={t("accounting.exceptions.dismissTitle")}
        description={t("accounting.exceptions.dismissDescription")}
      >
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (reason.trim().length >= 5) dismiss.mutate();
          }}
        >
          <FormField
            label={t("accounting.exceptions.dismissReason")}
            description={t("accounting.exceptions.dismissReasonHint")}
            required
          >
            {(control) => (
              <Textarea
                {...control}
                rows={3}
                maxLength={500}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                autoFocus
              />
            )}
          </FormField>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDismissing(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              variant="destructive"
              loading={dismiss.isPending}
              disabled={reason.trim().length < 5}
            >
              {t("accounting.exceptions.dismiss")}
            </Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
